/**
 * "What did the browser cost?", under the credential that spends it.
 *
 * Its own file rather than the bottom half of `BrowserRenderingCard`: that card
 * is a form about a secret and this is a read-only report, and together they
 * would be six hundred lines in which the save button and the money live in the
 * same scroll. The card renders this one and hands it the published rates it
 * already loaded.
 *
 * TODAY IS SEPARATE FROM THE PERIOD, and that is the whole point of the top
 * block. The free plan's wall is a DAILY one - ten browser minutes account-wide,
 * then captures simply stop until the day rolls over - so a month-to-date total
 * cannot answer the question somebody actually opens this for, which is whether
 * tonight's runs will go through.
 *
 * EVERY FIGURE HERE IS OUR OWN METERING. No Cloudflare quota, usage or billing
 * endpoint is read anywhere in the stack, so "left today" means left against our
 * own count of our own calls. That sentence is on screen, not only in this
 * comment, because a number that looks like a quota reading and is not is worse
 * than no number.
 */
import { useCallback, useEffect, useState } from "react";
import { Gauge, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getBrowserCost,
  type BrowserCostReport,
  type BrowserConsumer,
  type BrowserLimits,
} from "../../../../lib/browserRendering";
import { EmptyState, fmtNum, fmtDate } from "../../../ui";
import { IntegrationCard } from "../panel/IntegrationCard";
import { Note } from "../panel/fields";
// The money half is NOT written twice: `calls/cost/money.ts` already decides how
// many decimals a figure of this size deserves, and it is imported as a file
// rather than through the section's barrel so this panel does not drag the call
// desk's components into its module graph.
import { COST_DAYS, costPeriod, fmtMicros, type CostDays } from "../../calls/cost/money";
import { fmtBrowserMs, meterPct } from "./browserTime";

/** An hour in milliseconds, for turning the published allowance into hours. */
const HOUR_MS = 3_600_000;

/**
 * Which sentence names each spender. A lookup rather than a chain, for the same
 * reason the card's failure phrasing is one.
 *
 * The fallback is the raw ledger value, deliberately: a consumer added on the
 * server before the CRM learns its name has to draw as itself. A missing i18n
 * key here would be a table row that says nothing about where the allowance went.
 */
const CONSUMER_KEY: Record<BrowserConsumer, string> = {
  brain: "settings.browser.cost.consumerBrain",
  "template-thumb": "settings.browser.cost.consumerTemplateThumb",
  "wp-import": "settings.browser.cost.consumerWpImport",
  test: "settings.browser.cost.consumerTest",
  alert: "settings.browser.cost.consumerAlert",
  "platform-news": "settings.browser.cost.consumerPlatformNews",
  monitoring: "settings.browser.cost.consumerMonitoring",
};

/** One summary figure. Same shape as the call desk's cost screen. */
function Stat({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div className="crm-cost-stat">
      <span className="crm-cost-stat-l">{label}</span>
      <strong className="crm-cost-stat-v">{value}</strong>
      {note ? <span className="crm-cost-stat-n">{note}</span> : null}
    </div>
  );
}

export function BrowserCostPanel({ limits }: { limits: BrowserLimits }) {
  const t = useT();
  const [days, setDays] = useState<CostDays>(30);
  const [report, setReport] = useState<BrowserCostReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setReport(null);
    try {
      // `new Date()` at fetch time rather than in state, so a desk left open
      // overnight does not keep asking about yesterday.
      const { from, to } = costPeriod(days, new Date());
      setReport(await getBrowserCost(from, to));
      setError(null);
    } catch {
      setError(t("settings.browser.cost.loadError"));
    }
  }, [days, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // The rates the report does not carry come from the settings response the card
  // already holds. `paidIncludedMs` is used for both notes on purpose: on the
  // free plan `report.includedMs` is zero (there is no monthly allowance to
  // have), and the number somebody wants to see there is what Workers Paid would
  // include.
  const includedHours = Math.round(limits.paidIncludedMs / HOUR_MS);
  const overageRate = limits.paidOverageUsdPerHour.toFixed(2);

  return (
    <IntegrationCard
      icon={Gauge}
      title={t("settings.browser.cost.title")}
      desc={t("settings.browser.cost.desc")}
      status={
        <div className="flex items-center gap-2.5">
          {report === null && !error ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          ) : null}
          <div className="crm-seg">
            {COST_DAYS.map((option) => (
              <button
                key={option}
                type="button"
                className={cn("crm-seg-b", days === option && "is-on")}
                onClick={() => setDays(option)}
                aria-pressed={days === option}
              >
                {t("settings.browser.cost.days", { n: option })}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {error ? (
        <div className="mb-3 flex items-center gap-2 text-[12.5px] text-destructive">
          <TriangleAlert className="size-3.5 shrink-0" />
          {error}
        </div>
      ) : null}

      {report ? (
        <div className="space-y-5">
          <TodayMeter t={t} report={report} />

          {report.truncated ? (
            <Note tone="bad" icon={<TriangleAlert className="mt-0.5 size-4 shrink-0" />}>
              {t("settings.browser.cost.truncated")}
            </Note>
          ) : null}

          {report.calls === 0 ? (
            <EmptyState
              icon={Gauge}
              title={t("settings.browser.cost.emptyTitle")}
              body={t("settings.browser.cost.emptyNote")}
            />
          ) : (
            <PeriodTotals
              t={t}
              report={report}
              includedHours={includedHours}
              overageRate={overageRate}
            />
          )}

          {/* Always on screen, even on an empty period: it qualifies the zero
              above just as much as it qualifies a total. */}
          <p className="crm-cost-terms !mb-0">{t("settings.browser.cost.selfMetered")}</p>
        </div>
      ) : null}
    </IntegrationCard>
  );
}

/**
 * Today against the daily wall.
 *
 * Drawn as a bar as well as written, because "412 000 of 600 000" is a sentence
 * somebody has to do arithmetic on before it means anything.
 */
function TodayMeter({
  t,
  report,
}: {
  t: ReturnType<typeof useT>;
  report: BrowserCostReport;
}) {
  const { today } = report;
  const spent = today.remainingMs === 0;

  return (
    <section>
      <div className="crm-cost-stats !mb-2">
        <Stat
          label={t("settings.browser.cost.todayUsed")}
          value={fmtBrowserMs(today.browserMs)}
          note={t("settings.browser.cost.todayOf", { allowance: fmtBrowserMs(today.freeDailyMs) })}
        />
        <Stat
          label={t("settings.browser.cost.todayLeft")}
          value={fmtBrowserMs(today.remainingMs)}
          note={fmtDate(today.day)}
        />
      </div>

      <div className="crm-meter crm-meter-sm" aria-hidden="true">
        <i style={{ width: `${meterPct(today.browserMs, today.freeDailyMs)}%` }} />
      </div>

      {report.plan === "free" && spent ? (
        <Note tone="warn" className="mb-2">
          {t("settings.browser.cost.todaySpent")}
        </Note>
      ) : null}

      <p className="crm-cost-terms !mb-0">
        {report.plan === "paid"
          ? t("settings.browser.cost.todayNotePaid")
          : t("settings.browser.cost.todayNoteFree")}
      </p>
    </section>
  );
}

/** The period: how much ran, how much of it was guessed, and what it is worth. */
function PeriodTotals({
  t,
  report,
  includedHours,
  overageRate,
}: {
  t: ReturnType<typeof useT>;
  report: BrowserCostReport;
  includedHours: number;
  overageRate: string;
}) {
  const measuredMs = Math.max(0, report.browserMs - report.estimatedMs);
  // Never a bare "0.00" from a null: `fmtMicros` returns null for a figure
  // nobody could price, and that is a different claim from free.
  const overage = fmtMicros(report.overageMicros);

  return (
    <>
      <section>
        <div className="crm-cost-stats !mb-2">
          <Stat label={t("settings.browser.cost.calls")} value={fmtNum(report.calls)} />
          <Stat
            label={t("settings.browser.cost.failed")}
            value={fmtNum(report.failed)}
            note={report.failed > 0 ? t("settings.browser.cost.failedNote") : null}
          />
          <Stat
            label={t("settings.browser.cost.browserTime")}
            value={fmtBrowserMs(report.browserMs)}
            note={t("settings.browser.cost.measured", { ms: fmtBrowserMs(measuredMs) })}
          />
          <Stat
            label={t("settings.browser.cost.estimated")}
            value={fmtBrowserMs(report.estimatedMs)}
            note={
              report.estimatedCalls > 0
                ? t("settings.browser.cost.estimatedCalls", { n: report.estimatedCalls })
                : t("settings.browser.cost.allMeasured")
            }
          />
        </div>
        {/* The split, spelled out. A duration presented as measured when it was
            assumed is how a budget goes quietly wrong. */}
        <p className="crm-cost-terms !mb-0">{t("settings.browser.cost.estimatedNote")}</p>
      </section>

      <section>
        <div className="crm-cost-stats !mb-2">
          <Stat
            // Two different claims, so two different labels. On the free plan
            // nothing is billed at all and this figure is a what-if.
            label={
              report.notional
                ? t("settings.browser.cost.wouldCost")
                : t("settings.browser.cost.overage")
            }
            value={overage === null ? "-" : `${overage} USD`}
            note={t("settings.browser.cost.overMs", { ms: fmtBrowserMs(report.overMs) })}
          />
        </div>
        <p className="crm-cost-terms !mb-1">
          {report.notional
            ? t("settings.browser.cost.wouldCostNote", {
                rate: overageRate,
                hours: includedHours,
              })
            : t("settings.browser.cost.overageNote", { rate: overageRate, hours: includedHours })}
        </p>
        {/* Its own line, never folded into the total: the subscription is not a
            Browser Rendering charge and nothing on this screen causes it. */}
        <p className="crm-cost-terms !mb-0">
          {t("settings.browser.cost.baseFee", { base: report.paidBaseUsdPerMonth })}
        </p>
      </section>

      {/* Scrolls inside its own box: four consumers plus four numeric columns in
          a narrow reading pane is how a page ends up scrolling sideways whole. */}
      <div className="crm-cov-scroll">
        <table className="crm-cov-tbl crm-cost-tbl">
          <thead>
            <tr>
              <th scope="col">{t("settings.browser.cost.consumer")}</th>
              <th scope="col">{t("settings.browser.cost.calls")}</th>
              <th scope="col">{t("settings.browser.cost.failed")}</th>
              <th scope="col">{t("settings.browser.cost.browserTime")}</th>
              <th scope="col">{t("settings.browser.cost.estimated")}</th>
            </tr>
          </thead>
          <tbody>
            {report.byConsumer.map((row) => {
              const phrase = CONSUMER_KEY[row.key as BrowserConsumer];
              return (
                <tr key={row.key}>
                  <th scope="row">{phrase ? t(phrase) : row.key}</th>
                  <td>{fmtNum(row.calls)}</td>
                  <td>{fmtNum(row.failed)}</td>
                  <td>{fmtBrowserMs(row.browserMs)}</td>
                  <td>{fmtBrowserMs(row.estimatedMs)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
